import { useMemo } from 'react';
import type { Parameter } from '@shared/types';
import type { SceneLight, SceneOverrides, CameraPathWaypoint } from '@/worker/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScoreCriterion {
  key: string;
  /** Short label shown in the checklist */
  label: string;
  /** One-line description of what this criterion checks */
  description: string;
  /** Tip shown as "next step" when not met */
  tip: string;
  /** Whether the criterion is currently met */
  met: boolean;
  /** Point value — higher = more important to the score */
  weight: number;
}

export interface ProductScore {
  criteria: ScoreCriterion[];
  /** Weighted points earned */
  score: number;
  /** Maximum possible weighted score */
  max: number;
  /** 0–100 percentage */
  pct: number;
  /** Visual tier: <40% = low, <75% = mid, >=75% = high */
  tier: 'low' | 'mid' | 'high';
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export interface UseProductScoreInput {
  parameters: Parameter[];
  scadCode: string;
  sceneLights: SceneLight[];
  sceneOverrides: SceneOverrides;
  cameraPath: CameraPathWaypoint[];
  thumbnailUrl: string | null;
  isPublic: boolean;
  title: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useProductScore({
  parameters,
  scadCode,
  sceneLights,
  sceneOverrides,
  cameraPath,
  thumbnailUrl,
  isPublic,
  title,
}: UseProductScoreInput): ProductScore {
  return useMemo(() => {
    // --- detect features from scadCode via regex (fast, no parse overhead) ---
    const hasPbrMaterial =
      /scene_material\s*\(|material_def\s*\(|material\s*\(/.test(scadCode);
    const hasPipCameras = /camera_pip\s*\(/.test(scadCode);
    const hasSvgOverlays = /svg_overlay\s*\(/.test(scadCode);

    // Parameter-level checks
    const userParameters = parameters.filter((p) => p.type !== 'markdown');
    const hasMarkdown = parameters.some((p) => p.type === 'markdown');
    const hasSnapshots = /\/\*\s*\[Snapshots\]/i.test(scadCode);

    const criteria: ScoreCriterion[] = [
      // --- Fundamentals (weight 3) ---
      {
        key: 'has_title',
        label: 'Custom title',
        description: 'The model has a meaningful name (not "Untitled Model").',
        tip: 'Click the title field in the toolbar and give your model a descriptive name.',
        met: title.trim() !== '' && title.trim() !== 'Untitled Model',
        weight: 3,
      },
      {
        key: 'is_public',
        label: 'Published publicly',
        description: 'The model is set to Public so anyone can discover and view it.',
        tip: 'Click the globe / lock icon in the toolbar to make the model public.',
        met: isPublic,
        weight: 3,
      },
      {
        key: 'has_thumbnail',
        label: 'Saved thumbnail',
        description: 'A thumbnail is captured on save and shown in the Explore gallery.',
        tip: 'Save the model to generate a thumbnail from the current viewport.',
        met: thumbnailUrl !== null,
        weight: 3,
      },
      {
        key: 'has_parameters',
        label: 'User-facing parameters',
        description: 'At least one parameter control is exposed in the sidebar.',
        tip: 'Add a commented parameter line, e.g.  width = 50; // [10:200] Width',
        met: userParameters.length > 0,
        weight: 3,
      },

      // --- Scene quality (weight 2) ---
      {
        key: 'has_custom_lights',
        label: 'Custom lights',
        description: 'Uses light_ambient(), light_directional(), light_point(), or light_spot() for a tailored scene.',
        tip: 'Add light_point([x,y,z], color=[r,g,b], intensity=1.5); to set up scene lighting.',
        met: sceneLights.length > 0,
        weight: 2,
      },
      {
        key: 'has_pbr_material',
        label: 'PBR material',
        description: 'Uses scene_material(), material_def(), or material() for physically-based rendering.',
        tip: 'Add scene_material(metalness=0.4, roughness=0.3); near the top of your file.',
        met: hasPbrMaterial,
        weight: 2,
      },
      {
        key: 'has_camera_path',
        label: 'Intro camera path',
        description: 'A cinematic fly-in sequence plays automatically when the model first loads.',
        tip: 'Add camera_path([x,y,z], target=[0,0,0], transitions=["zoom_out"]); to create a fly-in.',
        met: cameraPath.length > 0,
        weight: 2,
      },
      {
        key: 'has_scene_camera',
        label: 'Custom camera position',
        description: 'scene_camera() sets the initial camera position and target for the best default view.',
        tip: 'Add scene_camera(position=[50,80,60], target=[0,0,0]); to set a good default view.',
        met: sceneOverrides.camera !== undefined,
        weight: 2,
      },

      // --- Polish (weight 1) ---
      {
        key: 'has_pip_cameras',
        label: 'Picture-in-picture cameras',
        description: 'camera_pip() viewports give users extra angle references without losing the main view.',
        tip: 'Add camera_pip(position=[0,0,100], target=[0,0,0]); to embed a secondary view.',
        met: hasPipCameras,
        weight: 1,
      },
      {
        key: 'has_markdown',
        label: 'Markdown descriptions',
        description: 'Formatted text, headings, links, or callout blocks in the parameters panel guide the user.',
        tip: 'Add // [markdown] // **About this model** above a parameter section.',
        met: hasMarkdown,
        weight: 1,
      },
      {
        key: 'has_snapshots',
        label: 'Parameter snapshots',
        description: 'Named presets let users jump between configurations in one click.',
        tip: 'Add a /* [Snapshots] Name = param:value, ... */ block to define presets.',
        met: hasSnapshots,
        weight: 1,
      },
      {
        key: 'has_svg_overlays',
        label: 'SVG overlays',
        description: 'svg_overlay() adds logos, watermarks, or labels on top of the 3D view.',
        tip: 'Add svg_overlay("logo.svg", x=0.02, y=0.02, w=0.1); after uploading an SVG file.',
        met: hasSvgOverlays,
        weight: 1,
      },
    ];

    const max = criteria.reduce((sum, c) => sum + c.weight, 0);
    const score = criteria.reduce((sum, c) => sum + (c.met ? c.weight : 0), 0);
    const pct = max > 0 ? Math.round((score / max) * 100) : 0;
    const tier: ProductScore['tier'] =
      pct >= 75 ? 'high' : pct >= 40 ? 'mid' : 'low';

    return { criteria, score, max, pct, tier };
  }, [
    parameters,
    scadCode,
    sceneLights,
    sceneOverrides,
    cameraPath,
    thumbnailUrl,
    isPublic,
    title,
  ]);
}
